import { IDte } from "src/app/models/Dte/IDte";
import { InvoiceType } from "src/app/models/Dte/invoiceType.enum";
import { esTipoCambioValido } from "src/app/components/facturacion/facturador-individual/dte-currency/dte-currency";

export interface DteValidationIssue {
	titulo: string;
	mensaje: string;
}

export interface DteValidationContext {
	dte: IDte;
	invoiceType: InvoiceType;
	tipoNota: number;
	referenciasActivas: boolean;
	requiereIdentificacionReceptor: boolean;
	valorUF135: number;
	grupoFolio?: { codigoSii?: number; suma?: number; nombre?: string };
	invalidSize: boolean;
	currentSize: number;
	totalDetalles: number;
	tieneDetallesConMontosInvalidos: boolean;
	esTransferenciaInterna: boolean;
	receptorTieneGiro: boolean;
	requiereGiroReceptor: boolean;
	correoReceptor?: string;
	proveedorValido: boolean;
	clienteExportacionValido: boolean;
	transporteIncompleto: boolean;
	exportacionInvalida: boolean;
	formularioNotaInvalido: boolean;
}

const CORREO_VALIDO = /^[A-Za-z0-9.!_~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9]{2,}(?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function validarDte(contexto: DteValidationContext): DteValidationIssue | null {
	const { dte, invoiceType, tipoNota } = contexto;
	const esCorreccionTexto = invoiceType === InvoiceType.NotaCredito && tipoNota === 2;
	const detalles = dte.detalleDte || [];

	if (!esCorreccionTexto && (detalles.length === 0 || detalles.every((detalle) => !detalle || Number(detalle.cantidad || 0) <= 0))) {
		return issue("Detalles vacíos", "No se ha ingresado ningún detalle para la emisión del documento.");
	}

	const referencias = dte.listaReferenciasDteEmitido || [];
	const referenciasIncompletas = referencias.some(referencia =>
		!referencia || referencia.isValid === false || !referencia.codigoTipoDteReferencia || referencia.codigoTipoDteReferencia === "0" || !String(referencia.folioReferencia ?? "").trim());
	if (referenciasIncompletas) {
		return issue("Referencias incompletas", "Complete los datos de las referencias ingresadas o elimine las filas que no desea incluir. En notas de crédito y débito debe indicar al menos una referencia completa.");
	}

	if (contexto.requiereIdentificacionReceptor && (receptorGenerico(dte) || dte.medioPagoBoletaEnum == null)) {
		return issue("Identificación del Receptor Obligatoria", `Esta boleta tiene un valor de $${Number(dte.total || 0).toLocaleString()} lo cuál supera las 135 UF ($${Number(contexto.valorUF135 || 0).toLocaleString()}). Según Resolución SII N°44, debe indicar los datos del receptor e indicar medio de pago.`);
	}

	if (!contexto.grupoFolio) return issue("Sin registro de folios", "No hay registro de folios disponibles para emitir el tipo de documento actual");
	if (contexto.grupoFolio.codigoSii === dte.codigoTipoDte && contexto.grupoFolio.suma === 0) {
		return issue("Folios insuficientes", "No hay folios disponibles para emitir el tipo de documento " + (contexto.grupoFolio.nombre || "seleccionado"));
	}
	if (!dte.tipoDteId) return issue("Omisión de Datos", "No se ha seleccionado el tipo de documento a generar");
	if (dte.otraMonedaEnt?.tipoMoneda && dte.otraMonedaEnt.tipoMoneda !== "CLP" && !esTipoCambioValido(dte.otraMonedaEnt.tipoCambio)) {
		return issue("Error en valor de moneda", "Falta una cotización válida. Vuelva a cargar el valor de la moneda o ingrese un valor mayor que cero antes de emitir.");
	}
	if (!esCorreccionTexto && !contexto.esTransferenciaInterna && contexto.totalDetalles <= 0) {
		return issue("Error en Montos", "El total de detalles indicado debe ser mayor a cero.");
	}
	if (!esCorreccionTexto && !contexto.esTransferenciaInterna && contexto.tieneDetallesConMontosInvalidos) {
		return issue("Error en Montos", "Los elementos de detalle indicados no pueden tener valores menores a cero.");
	}
	if (Number(dte.neto || 0) <= 5 && Number(dte.exento || 0) <= 5 && dte.total !== dte.exento && invoiceType !== InvoiceType.FacturaExportacion && invoiceType !== InvoiceType.LiquidacionFactura && Number(dte.descuentoGlobal || 0) === 0) {
		return issue("Error en Montos", "El neto total del documento debe ser mayor a $5");
	}
	if (Number(dte.formato || 0) === 0) {
		return issue("Error en Formato", "No ha asignado una plantilla para el tipo de documento seleccionado o el formato a utilizar considera 0 líneas de detalle");
	}
	if (contexto.invalidSize) {
		return issue("Error en Detalles", `Los elementos de detalle son mayores a los permitidos en el formato del documento. La cantidad máxima es de ${dte.formato} y se están utilizando ${contexto.currentSize} líneas de detalle.`);
	}

	const requiereClienteNacional = invoiceType !== InvoiceType.Boleta && invoiceType !== InvoiceType.FacturaCompra && invoiceType !== InvoiceType.FacturaExportacion && invoiceType !== InvoiceType.NotaCredito;
	if ((requiereClienteNacional && receptorGenerico(dte)) || (invoiceType === InvoiceType.FacturaExportacion && !contexto.clienteExportacionValido)) {
		return issue("Error en Cliente", "No se ha asignado correctamente el cliente asociado al documento, por favor intente nuevamente");
	}
	if (invoiceType === InvoiceType.FacturaCompra && !contexto.proveedorValido) {
		return issue("Error en Proveedor", "No se ha asignado correctamente el proveedor asociado al documento, por favor intente nuevamente");
	}
	if (contexto.requiereGiroReceptor && !contexto.receptorTieneGiro) {
		return issue("Falta giro del receptor", "Debe completar el giro del receptor antes de generar este tipo de DTE.");
	}
	if (contexto.correoReceptor && !CORREO_VALIDO.test(contexto.correoReceptor)) {
		return issue("Correo no válido", "El correo electrónico ingresado no tiene un formato válido.");
	}
	if (!dte.tipoMoneda) return issue("Error en Divisa", "No se ha asignado correctamente la divisa asociada al documento, por favor intente nuevamente");
	if (Number(dte.descuentoGlobal || 0) > contexto.totalDetalles) return issue("Error en descuento", "El descuento ingresado es mayor al monto total de detalles del documento");
	/*if (dte.indicadorMontosNetos === false && Number(dte.impuestos || 0) > 0) {
		return issue("Error en Montos con Impuestos", "Si los productos ingresados tienen impuestos específicos, no puede indicarse que sean productos con impuestos incluidos; solamente pueden ser a valor neto");
	}*/
	if (invoiceType === InvoiceType.GuiaDespacho && contexto.transporteIncompleto) return issue("Datos de Transporte Incompletos", "Ingrese todos los datos correspondientes al módulo de transporte");
	if (invoiceType === InvoiceType.FacturaExportacion && contexto.exportacionInvalida) return issue("Error en datos de Exportación", "Los datos de la sección de transporte se encuentran incompletos, favor revisar y completar e intente emitir la factura de exportación nuevamente");

	if (invoiceType === InvoiceType.NotaDebito || invoiceType === InvoiceType.NotaCredito) {
		const nombreNota = invoiceType === InvoiceType.NotaCredito ? "Nota de Crédito" : "Nota de Débito";
		if (contexto.formularioNotaInvalido) return issue("Error en emisión de " + nombreNota, "Debe indicar un motivo para emitir la " + nombreNota.toLocaleLowerCase() + ".");
		if (!dte.listaReferenciasDteEmitido || dte.listaReferenciasDteEmitido.length === 0) return issue("Error en emisión de " + nombreNota, "Debe seleccionar al menos un documento de referencia para emitir la " + nombreNota.toLocaleLowerCase() + ".");
	}

	return null;
}

function receptorGenerico(dte: IDte): boolean {
	return !dte.rutReceptor || dte.rutReceptor === "66666666";
}

function issue(titulo: string, mensaje: string): DteValidationIssue {
	return { titulo, mensaje };
}
